/**
 * dev-auth-personas.ts — FE mirror of server/src/lib/dev-auth-personas.ts.
 *
 * Keep slugs, tokens, userId, role, and companyId in sync with the server module so
 * local dev bypass hits the same RBAC surface as Cindy’s Stage QA accounts.
 */

import type { UserSession } from '@/types/auth';

export type DevAuthPersona = {
	slug: string;
	label: string;
	token: string;
	userId: number;
	email: string;
	role: string;
	companyId: number;
	companyName: string;
	firstName: string;
	lastName: string;
};

const ROLE_ID: Record<string, number> = {
	SSIAdministrator: 6,
	SSIEmployee: 5,
	CompanyAdministrator: 4,
	CompanyManager: 11,
	User: 3,
	ReadOnly: 10,
};

/** Must match server/src/lib/dev-auth-personas.ts DEV_AUTH_PERSONAS. */
export const DEV_AUTH_PERSONAS: readonly DevAuthPersona[] = [
	{
		slug: 'ssi',
		label: 'SSI Admin',
		token: 'dev-token',
		userId: 900,
		email: 'admin@stevensonsystems.com',
		role: 'SSIAdministrator',
		companyId: 2,
		companyName: 'Stevenson Systems',
		firstName: 'Admin',
		lastName: 'SSI',
	},
	{
		slug: 'se',
		label: 'SSI Employee',
		token: 'dev-token:se',
		userId: 9000391,
		email: 'employee@stevensonsystems.com',
		role: 'SSIEmployee',
		companyId: 2,
		companyName: 'Stevenson Systems',
		firstName: 'SSI',
		lastName: 'Employee',
	},
	{
		slug: 'cm',
		label: 'Company Manager',
		token: 'dev-token:cm',
		userId: 5954,
		email: 'managertest@ssicorporate.com',
		role: 'CompanyManager',
		companyId: 3906,
		companyName: 'SSI Corporate (Demo)',
		firstName: 'Manager',
		lastName: 'Test',
	},
	{
		slug: 'ca',
		label: 'Company Admin',
		token: 'dev-token:ca',
		userId: 6032,
		email: 'testcompanyadmin@ssicorporate.com',
		role: 'CompanyAdministrator',
		companyId: 3906,
		companyName: 'SSI Corporate (Demo)',
		firstName: 'Company',
		lastName: 'Admin',
	},
	{
		slug: 'cu',
		label: 'Company User',
		token: 'dev-token:cu',
		userId: 8130,
		email: 'usertest@ssicorporate.com',
		role: 'User',
		companyId: 3906,
		companyName: 'SSI Corporate (Demo)',
		firstName: 'User',
		lastName: 'Test',
	},
	{
		slug: 'lu',
		label: 'Limited / Read-only',
		token: 'dev-token:lu',
		userId: 5845,
		email: 'prodtest@ssicorporate.com',
		role: 'ReadOnly',
		companyId: 3906,
		companyName: 'SSI Corporate (Demo)',
		firstName: 'Limited',
		lastName: 'Test',
	},
];

export const DEV_AUTH_PERSONA_STORAGE_KEY = 'truspace.devAuthPersona';

const PERSONA_BY_SLUG = new Map(DEV_AUTH_PERSONAS.map((p) => [p.slug, p]));

/** True when the login page should show the local QA persona picker. */
export function isDevAuthPersonasEnabled(): boolean {
	return (
		import.meta.env.DEV &&
		import.meta.env.VITE_DEV_AUTH === 'true' &&
		import.meta.env.VITE_DEV_AUTH_PERSONAS === 'true'
	);
}

/** Read persisted persona slug (defaults to SSI). */
export function getStoredDevPersonaSlug(): string {
	if (typeof window === 'undefined') return 'ssi';
	return window.localStorage.getItem(DEV_AUTH_PERSONA_STORAGE_KEY) ?? 'ssi';
}

export function getDevPersonaBySlug(slug: string): DevAuthPersona | undefined {
	return PERSONA_BY_SLUG.get(slug);
}

/** Active dev persona for this browser session. */
export function getActiveDevPersona(): DevAuthPersona {
	return getDevPersonaBySlug(getStoredDevPersonaSlug()) ?? DEV_AUTH_PERSONAS[0];
}

export function persistDevPersonaSlug(slug: string): void {
	if (typeof window === 'undefined') return;
	window.localStorage.setItem(DEV_AUTH_PERSONA_STORAGE_KEY, slug);
}

/** Build a UserSession the auth store can hydrate from a dev persona. */
export function devPersonaToSession(persona: DevAuthPersona): UserSession {
	return {
		userId: persona.userId,
		temporaryUsersId: null,
		username: persona.email,
		email: persona.email,
		token: persona.token,
		role: persona.role,
		roleId: ROLE_ID[persona.role] ?? 0,
		companyId: persona.companyId,
		companyName: persona.companyName,
		firstName: persona.firstName,
		lastName: persona.lastName,
		name: `${persona.firstName} ${persona.lastName}`.trim(),
		isActiveDirectory: false,
		isSessionActivated: true,
		isApproved: true,
		active: true,
	};
}
